/**
 * T1 / docs/49 §8 — M-00 首頁儀表板聚合型別(slice-1)。
 * -----------------------------------------------------------------------------
 * 對齊後端 `GET /api/dashboard`(group-scoped,L1.5 帶 `X-Current-Group`)。
 * slice-1 = 三類 alert 計數 + 素材庫總數;本週成效摘要(費用/CTR/CPC + trend)= slice-2。
 */

// ─── DTO（snake_case）────────────────────────────────────────────────────────

export interface DashboardSummaryDto {
  /** alert ① — 需重新授權的廣告帳戶數(`status=TOKEN_EXPIRED`)。 */
  oauth_needs_reauth_count: number;
  /**
   * alert ①-b — **在 Google 端未啟用**的廣告帳戶數(`sync_status=ACCOUNT_NOT_ENABLED`
   * 的 ACTIVE 帳戶;BE `41cd785` / v22-18,2026-09-02)。
   *
   * ⚠️ **與 `oauth_needs_reauth_count` 刻意分開**(BE 明寫同一條理由):一個要去
   * Google Ads 啟用、一個要重新授權,混成一個數字使用者會去按沒用的那個。
   * ⚠️ **optional**:BE 那批尚未併 main(beta / prod 沒有)→ 缺欄留 `null`,那張卡不畫。
   */
  account_not_enabled_count?: number;
  /** alert ② — 尚未推上 Google Ads 的素材庫 item 數。 */
  unimported_asset_count: number;
  /** alert ③ — AEI 週增 ≥ +10(惡化)的素材數;`null` = AEI 暫不可用(graceful)。 */
  aei_worsening_count: number | null;
  /** 當前 (user, group) 素材庫 item 總數。 */
  library_total: number;
  /**
   * 檢查三計數(BE `dev-update-v21-3`,2026-08-30)。與 `library_total` 同一次掃描。
   *
   * ⚠️ **三數相加 ≠ `library_total` 是正常的** —— badge 為 pass / unk / err 的 item
   * 不入三計數;COMPOSE 等不可檢種類也不入「未檢查」(否則永遠掛著)。
   * ⚠️ **optional** —— BE 這批**尚未併 main**(beta 沒有),缺欄時三張卡不畫。
   */
  check_fail_count?: number;
  check_warn_count?: number;
  check_unchecked_count?: number;
  /** 「本月用量」卡;`null` = 錢包讀不到(FE 整張卡不畫)。缺欄同 null。 */
  usage?: UsageSummaryDto | null;
}

/**
 * 「本月用量」的兩種模式(BE `UsageSummary`)。
 * - `credits` = 個人 / 非合約工作區 → 畫「剩餘點數」= `available`,**不畫百分比**
 *   (個人錢包沒有月配額,湊一個百分比出來是假的經營指標)。
 * - `quota` = 合約工作區 → 百分比 = `quota_used / quota_total`,剩餘天數由 `period_end` 算。
 *
 * ⚠️ beta 現況所有工作區都是個人 → 上線初期只會看到 `credits`。
 */
export interface UsageSummaryDto {
  mode: 'credits' | 'quota';
  balance: number;
  reserved: number;
  available: number;
  /** quota 模式限定;credits 模式為 null。 */
  quota_total?: number | null;
  quota_used?: number | null;
  /** 下次派點 / reset 日(ISO);null = 不顯示剩餘天數。 */
  period_end?: string | null;
}

// ─── camelCase ────────────────────────────────────────────────────────────────

export interface DashboardSummary {
  oauthNeedsReauthCount: number;
  /**
   * 在 Google 端未啟用的廣告帳戶數;**`null` = 這個 BE 還沒有這一欄**(不是 0)。
   * 呈現端一律「null → 不畫」—— 畫 0 會被讀成「查過而且都啟用了」。
   */
  accountNotEnabledCount: number | null;
  unimportedAssetCount: number;
  /** `null` = AEI 暫不可用(BQ 失敗 / 無同步成效資料)。 */
  aeiWorseningCount: number | null;
  libraryTotal: number;
  /**
   * 檢查三計數;**`null` = 這個 BE 還沒有這一欄**(不是 0)。
   * ⚠️ 呈現端一律「null → 整張卡不畫」—— 畫 0 會被讀成「檢查過而且沒問題」,
   * 那比空白更糟(R292.17 起的既有判準)。
   */
  checkFailCount: number | null;
  checkWarnCount: number | null;
  checkUncheckedCount: number | null;
  /** `null` = 錢包讀不到 / 舊 BE 缺欄 → 整張卡不畫。 */
  usage: UsageSummary | null;
}

export interface UsageSummary {
  mode: 'credits' | 'quota';
  balance: number;
  reserved: number;
  available: number;
  /** quota 模式限定;credits 模式恆 `null`。 */
  quotaTotal: number | null;
  quotaUsed: number | null;
  periodEnd: string | null;
}

// ─── DTO → camelCase mapping ─────────────────────────────────────────────────

export function mapDashboardSummaryDto(dto: DashboardSummaryDto): DashboardSummary {
  return {
    // 防御:BE 理應給數字,但 transient / 漂值時 `?? 0` normalize(對齊 mapper 邊界慣例);
    // aeiWorseningCount 保留 null 語意(AEI 不可用),不可 `?? 0` 誤當「0 件惡化」。
    oauthNeedsReauthCount: dto.oauth_needs_reauth_count ?? 0,
    // ⚠️ 與上面那欄的 `?? 0` **刻意不同**:這一欄是「這個 BE 版本有沒有做」的訊號
    // (BE `41cd785` 未併 main),`?? 0` 會把「沒有這一欄」翻譯成「0 個未啟用帳戶」。
    accountNotEnabledCount: numOrNull(dto.account_not_enabled_count),
    unimportedAssetCount: dto.unimported_asset_count ?? 0,
    aeiWorseningCount: typeof dto.aei_worsening_count === 'number' ? dto.aei_worsening_count : null,
    libraryTotal: dto.library_total ?? 0,
    // ⚠️ 這三欄與上面幾欄的 `?? 0` **刻意不同**:上面那些 BE 恆給(漂值才防禦),
    // 這三欄則是「這個 BE 版本有沒有做」的訊號 —— `?? 0` 會把「沒有這一欄」翻譯成
    // 「0 件待處理」,而 0 在這一區的語意是「檢查過而且沒問題」。缺欄必須留 null。
    checkFailCount: numOrNull(dto.check_fail_count),
    checkWarnCount: numOrNull(dto.check_warn_count),
    checkUncheckedCount: numOrNull(dto.check_unchecked_count),
    usage: mapUsageSummaryDto(dto.usage),
  };
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function mapUsageSummaryDto(dto: UsageSummaryDto | null | undefined): UsageSummary | null {
  if (!dto || (dto.mode !== 'credits' && dto.mode !== 'quota')) return null;
  return {
    mode: dto.mode,
    balance: dto.balance ?? 0,
    reserved: dto.reserved ?? 0,
    available: dto.available ?? 0,
    quotaTotal: numOrNull(dto.quota_total),
    quotaUsed: numOrNull(dto.quota_used),
    periodEnd: typeof dto.period_end === 'string' && dto.period_end ? dto.period_end : null,
  };
}
